/**
 * API service configuration
 *
 * @author Claude
 */
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';

// Environment configuration with validation
const getApiConfig = () => {
  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8847';
  const timeout = process.env.REACT_APP_API_TIMEOUT
    ? parseInt(process.env.REACT_APP_API_TIMEOUT, 10)
    : 30000;
  const isProduction = process.env.NODE_ENV === 'production';

  // Validate API URL format (allow relative paths like /api for same-origin deployments)
  if (!baseUrl.startsWith('/') || baseUrl.startsWith('//')) {
    try {
      new URL(baseUrl);
    } catch (error) {
      console.error('Invalid API URL format:', baseUrl);
      throw new Error(`Invalid API URL configuration: ${baseUrl}`);
    }
  }

  // Warn about development configuration in production
  if (isProduction && baseUrl.includes('localhost')) {
    console.warn('Warning: Using localhost API URL in production environment');
  }

  // Log configuration in development
  if (process.env.NODE_ENV === 'development') {
    console.log('API Configuration:', {
      baseUrl,
      timeout,
      environment: process.env.NODE_ENV,
      version: process.env.REACT_APP_VERSION || 'unknown',
    });
  }

  return { baseUrl, timeout };
};

const { baseUrl: BASE_URL, timeout: TIMEOUT } = getApiConfig();

// CSRF token management
let csrfToken: string | null = null;

/**
 * Get CSRF token from cookies
 */
const getCSRFTokenFromCookie = (): string | null => {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'XSRF-TOKEN') {
      return decodeURIComponent(value);
    }
  }
  return null;
};

/**
 * Fetch CSRF token from server
 */
const fetchCSRFToken = async (): Promise<string | null> => {
  try {
    const response = await axios.get(`${BASE_URL}/auth/csrf-token`, {
      withCredentials: true,
    });
    csrfToken = response.data.csrfToken;
    return csrfToken;
  } catch (error) {
    console.error('Failed to fetch CSRF token:', error);
    return null;
  }
};

/**
 * Create configured axios instance for API calls
 */
export const createApiClient = (token?: string | null): AxiosInstance => {
  const config: AxiosRequestConfig = {
    baseURL: BASE_URL,
    timeout: TIMEOUT,
    withCredentials: true, // Include cookies for CSRF protection
    headers: {
      'Content-Type': 'application/json',
    },
  };

  // Add auth token if available
  if (token) {
    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${token}`,
    };
  }

  const client = axios.create(config);

  // Add request interceptor for CSRF token
  client.interceptors.request.use(
    async config => {
      // Add CSRF token for state-changing requests
      if (config.method && !['get', 'head', 'options'].includes(config.method.toLowerCase())) {
        let token = getCSRFTokenFromCookie();

        // If no token in cookie, try to fetch one
        if (!token && !csrfToken) {
          token = await fetchCSRFToken();
        }

        // Use cached token if available
        if (!token && csrfToken) {
          token = csrfToken;
        }

        if (token) {
          config.headers['X-XSRF-TOKEN'] = token;
        }
      }

      return config;
    },
    error => Promise.reject(error)
  );

  // Add response interceptor for consistent error handling and rate limit tracking
  client.interceptors.response.use(
    response => {
      // Notify rate limit hook of response headers
      if (response.headers && typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('api-response', {
            detail: { headers: response.headers },
          })
        );
      }
      return response;
    },
    async (error: AxiosError) => {
      // Handle specific error cases
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('API Error Response:', {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers,
        });

        // Extract error message from various API response formats
        const apiError = error.response.data as any;
        if (apiError) {
          if (typeof apiError === 'string') {
            error.message = apiError;
          } else if (apiError.message) {
            error.message = apiError.message;
          } else if (apiError.error && apiError.error.message) {
            error.message = apiError.error.message;
          }
        }

        // Special handling for 401 Unauthorized
        if (error.response.status === 401) {
          console.warn('Authentication required. Redirecting to login...');
          // Let the auth context handle the redirect
        }

        // Special handling for 403 CSRF errors
        if (error.response.status === 403) {
          const apiError = error.response.data as any;
          if (apiError?.error?.code?.includes('csrf')) {
            console.warn('CSRF token invalid. Refreshing token...');
            // Clear cached token and try to fetch a new one
            csrfToken = null;
            await fetchCSRFToken();
          }
        }

        // Notify rate limit hook of error response headers (especially for 429)
        if (error.response.headers && typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('api-error', {
              detail: {
                status: error.response.status,
                headers: error.response.headers,
                error,
              },
            })
          );
        }
      } else if (error.request) {
        // The request was made but no response was received
        console.error('API No Response:', error.request);
        error.message = 'No response from server. Please check your connection.';
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('API Request Error:', error.message);
      }

      return Promise.reject(error);
    }
  );

  return client;
};

// Export default instance without auth (only in non-test environment)
export const api = process.env.NODE_ENV !== 'test' ? createApiClient() : null;

// Export as default for services that import as default
export default api;

// Export as apiClient for services that import with that name
export const apiClient = api;
